import React, { useEffect, useState } from "react";

import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import "../../../../shared/styles/ag-grid/ag-grid.styles.css";
import {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy, SizeColumnsToFitProvidedWidthStrategy
} from "ag-grid-community";

import AgGridService from "../../../../shared/services/ag-grid/ag-grid.service";
import useHelpers from "../../../../shared/services/helpers/useHelpers.hook";
import { useGetReportDataApi } from "./api/useGetReportDataApi.hook";
import { useGetReportDetailsApi } from "./api/useGetReportDetailsApi.hook";
import useAppStoreContext from "../../../../shared/context/app-store/app-store.context";
import ReportDataFunctionsDialogComponent from "./components/report-data-functions-dialog/report-data-functions-dialog.component";
import ReportDetailFiltersDialogComponent from "./components/report-data-filters-dialog/report-data-filters-dialog.component";
import ReportDataToolbarComponent from "./components/report-data-toolbar/report-data-toolbar.component";
import WizardReportComponent from "../../../components/report-details-wizard/wizard-report.component";
import useReportDataService from "./useReportDataService.hook";


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function ReportDataPage({ }: {}) {

  const { IS_NOT_EMPTY } = useHelpers();

  const { ReportDetailsLoadingIndicator, reportDetailsLoader } = useGetReportDetailsApi();
  // ---
  const { ReportDataProgressIndicator, reportDataLoader } = useGetReportDataApi();

  const [showColumnsDialog, setShowColumnsDialog] = useState<boolean>(false);
  const [showFiltersDialog, setShowFiltersDialog] = useState<boolean>(false);
  const [showReportWizard, setShowReportWizard] = useState(false);

  const { getDefaultProps, getDefaultColDef } = new AgGridService();
  const [paramsReloadTrigger, setParamsReloadTrigger] = useState<number>(0);
  const [reportReloadTrigger, setReportReloadTrigger] = useState<number>(0);

  const { colDefs, rowStyles, styleInfo, onModelUpdated } = useReportDataService();

  const {
    isReportSelected, selReportCode, selReportData, resetFiltersAndColumns,
    currentAvailableColumns, currentSelectedColumns, currentFilters,
  } = useAppStoreContext();

  // ---Load-Params----------------------------
  const loadReportParams = () => {
    if (selReportCode) {
      reportDetailsLoader.loadAsync();
    }
  }

  useEffect(() => {
    if (paramsReloadTrigger) {
      loadReportParams();
    }
  }, [paramsReloadTrigger])

  useEffect(() => {
    loadReportParams()
  }, [selReportCode])


  // ---Load-ReportData----------------------------
  const loadReportData = () => {

    const isParamsNotLoading = (reportDetailsLoader.isNotLoading);

    const isParamsNotEmpty = (IS_NOT_EMPTY(currentSelectedColumns)
      && IS_NOT_EMPTY(currentAvailableColumns));

    const isParamsDialogNotShowing = !(showColumnsDialog || showFiltersDialog);

    if (isParamsNotLoading && isParamsDialogNotShowing && isParamsNotEmpty
      && isReportSelected && reportDataLoader.isNotLoading
    ) {
      reportDataLoader.loadAsync();
    }
  }

  useEffect(() => {
    if (reportReloadTrigger) { loadReportData(); }
  }, [reportReloadTrigger])

  useEffect(() => {
    loadReportData();
  }, [
    showColumnsDialog, showFiltersDialog,
    currentSelectedColumns, currentAvailableColumns, currentFilters,
    reportDetailsLoader.isNotLoading,
  ])

  const handleOnReset = () => resetFiltersAndColumns();
  const handleApplyFunctionDialog = () => setShowColumnsDialog(false);
  const handleApplyFiltersDialog = () => setShowFiltersDialog(false);
  const handleWizardClose = (reloadAll: boolean) => {
    if (reloadAll) { setParamsReloadTrigger(prev => prev + 1); }
    setShowReportWizard(false);
  }


  // ---
  return (<>

    {/* Toolbar */}
    <ReportDataToolbarComponent
      onReset={handleOnReset} onReload={() => setReportReloadTrigger(prev => prev + 1)}
      handleEditReportClick={() => setShowReportWizard(true)}
      handleColumnsClick={() => setShowColumnsDialog(true)}
      handleFiltersClick={() => setShowFiltersDialog(true)}
    />

    {/* The AG Grid component */}
    <div className="ag-theme-quartz" style={{
      height: "85vh", width: "100%",
      maxWidth: "1800px", margin: "auto", padding: "1rem",
    }}>

      <AgGridReact {...getDefaultProps({})}
        suppressFieldDotNotation={true} // prevents treating '.' as nested property in ([INDIRECTSALESVIEW].[BUYING_GROUP_ID])
        columnDefs={colDefs} rowData={selReportData || []}
        defaultColDef={{ ...getDefaultColDef(), resizable: true, }}
        onModelUpdated={onModelUpdated}
        getRowStyle={rowStyles}
        // Enable pagination
        paginationPageSize={Number(styleInfo?.itemsPerPage ?? 20)} // Rows per page
        paginationPageSizeSelector={false}
        pagination={styleInfo?.usePaginationInWebView?.toLowerCase() === 'true' &&
          Number(styleInfo.itemsPerPage) > 0
        }
      />
    </div>

    {/* *****-DIALOGS-***** */}

    {/* Redesigned-Report Wizard */}
    <WizardReportComponent reportCode={selReportCode} open={showReportWizard} onClose={handleWizardClose} />

    {/* Loading Indicators */}
    <ReportDetailsLoadingIndicator />
    {/* --- */}
    <ReportDataProgressIndicator />


    {/* Columns-Dialog */}
    {showColumnsDialog && (<ReportDataFunctionsDialogComponent
      open={showColumnsDialog}
      onClose={() => setShowColumnsDialog(false)}
      onApply={() => handleApplyFunctionDialog()}
    />
    )}

    {/* Filters-Dialog */}
    {showFiltersDialog && (<ReportDetailFiltersDialogComponent
      open={showFiltersDialog}
      onClose={() => setShowFiltersDialog(false)}
      onApply={() => handleApplyFiltersDialog()}
    />
    )}

  </>);
}